import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  Game,
  GameHandballPosition,
  GameHandballPositionLocalization,
  GameStatuses,
  GameUser,
  generateArray,
  getEnumOptions,
  HandballGameLog,
  HandballGameLogTypes,
  HandballGameStatistic,
  Team,
  Tournament
} from '@mtgame/core';
import { getHeaderHeight } from '@utils/layout';
import { getLeaguePlayerRoute, getTeamPageRoute } from '@config/routes';

@Component({
  selector: 'mtg-handball-game-protocol',
  templateUrl: './handball-game-protocol.component.html',
  styleUrls: ['./handball-game-protocol.component.scss', '../../game/styles/protocol.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HandballGameProtocolComponent implements OnInit, OnChanges {
  @Input()
  users: GameUser[] = [];
  @Input()
  gameStatistic: HandballGameStatistic[] = [];
  @Input()
  logs: HandballGameLog[] = [];
  @Input()
  team: Team;
  @Input()
  game: Game;
  @Input()
  tournament: Tournament;
  @Input()
  dialog: boolean;

  players: GameUser[] = [];
  goalkeepers: GameUser[] = [];
  filteredUsers: GameUser[] = [];
  filteredStatistic: {[key: number]: HandballGameStatistic} = {};
  periods: number[] = [];
  userEfficiency = {};
  userMinutes = {};
  overtimePeriods: number[] = [];
  positionLocalization = GameHandballPositionLocalization;
  positions = getEnumOptions(GameHandballPosition);
  periodControl = new UntypedFormControl(null);
  positionControl = new UntypedFormControl(null);
  query = new UntypedFormControl();
  headerHeight = getHeaderHeight();

  constructor(
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.periodControl.valueChanges
      .subscribe(() => {
        this.updateProtocol();
      });
    this.positionControl.valueChanges
      .subscribe(() => {
        this.updateProtocol();
      });
    this.query.valueChanges
      .subscribe(() => {
        this.updateProtocol();
      });
    this.periods = generateArray(this.game.handballGameConfig.periodsCount);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.users || changes.logs) {
      this.updateLogs();
    }
    if ((changes.users || changes.logs || changes.period || changes.gameStatistic) &&
      this.users && this.logs && this.users.length && this.gameStatistic
    ) {
      this.updateProtocol();
    }
  }

  getTotal(key: string): number {
    const gameUserIds = this.users.map(u => u.id);
    return Object.keys(this.filteredStatistic).filter(k => gameUserIds.indexOf(+k) > -1)
      .reduce((acc, gameUserId) => acc + (this.filteredStatistic[gameUserId][key] || 0), 0);
  }

  getUserProfileRoute(gameUser: GameUser): string[] {
    return getLeaguePlayerRoute(gameUser.tournamentTeamUser.leaguePlayer.id);
  }

  getTeamRoute(teamId: number): string[] {
    return getTeamPageRoute(teamId);
  }

  updateLogs() {
    if (!this.logs || !this.users) {
      return;
    }
    this.logs = this.logs.map(item => {
      item.gameUser = this.users.find(u => u.id === item.gameUserId);
      return item;
    });
  }

  updateProtocol() {
    this.overtimePeriods = (this.logs || []).filter(l => l.period > this.game.handballGameConfig.periodsCount)
      .map(l => l.period)
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort();
    this.filteredUsers = this.getFilteredUsers();
    let players = [];
    let goalkeepers = [];
    players = this.filteredUsers.filter(item => item.teamUser.user.handballProfile.position !== GameHandballPosition.goalkeeper);
    goalkeepers = this.filteredUsers.filter(item => item.teamUser.user.handballProfile.position === GameHandballPosition.goalkeeper);
    this.players = players;
    this.goalkeepers = goalkeepers;

    this.filteredStatistic = this.getFilteredStatistic();
    this.calculateAdditionalStatistic();
    this.cdr.markForCheck();
  }

  getFilteredUsers(): GameUser[] {
    const query = (this.query.value || '').toLowerCase();
    const position = +this.positionControl.value;

    return this.users.filter(item =>
      (!position || item.teamUser.user.handballProfile.position === position) &&
      (!query || item.tournamentTeamUser.id
        ? item.tournamentTeamUser.leaguePlayer.shortName.toLowerCase().indexOf(query) > - 1
        : item.teamUser.user.shortName.toLowerCase().indexOf(query)
      )
    );
  }

  getFilteredStatistic(): {[key: number]: HandballGameStatistic} {
    const period = this.periodControl.value;
    if (!period) {
      return (this.gameStatistic || []).reduce((acc, item) =>
        Object.assign(acc, {[item.gameUserId]: item}), {});
    }
    const logs = this.logs.filter(item => item.period === +period);
    return (this.gameStatistic || []).reduce((acc, item) =>
      Object.assign(acc, {[item.gameUserId]: this.updateGameUserStats(Object.assign(new HandballGameStatistic(), item), logs)}), {}
    );
  }

  calculateAdditionalStatistic() {
    const period = +this.periodControl.value;
    const logs = period
      ? this.logs.filter(item => item.period === period)
      : this.logs;
    const enterExitLogs = this.logs.filter(item => [HandballGameLogTypes.enter_game, HandballGameLogTypes.exit_game].indexOf(item.logType) > -1);
    const teamUserIds = this.users.filter(u => u.teamUser.teamId === this.game.team.id).map(u => u.id);
    const competitorUserIds = this.users.filter(u => u.teamUser.teamId === this.game.competitorTeam.id).map(u => u.id);
    const scoreLogs = logs.filter(item => item.isScoreType());
    const lastLog = this.logs.length > 0 ? this.logs[0] : null;
    if (!lastLog) {
      const emptyData = this.users.reduce((acc, item) => Object.assign(acc, {[item.id]: 0}), {});
      this.userEfficiency = emptyData;
      this.userMinutes = emptyData;
      return;
    }

    for (const user of this.users) {
      const userLogs = enterExitLogs.filter(item => item.gameUserId === user.id).reverse();
      const periods = userLogs.filter(item => item.logType === HandballGameLogTypes.enter_game).map(item => {
        const exitLog = userLogs.find(i => {
          if (i.logType !== HandballGameLogTypes.exit_game) {
            return false;
          }
          if (i.time === item.time && i.period === item.period) {
            return i.id > item.id;
          }
          return i.time > item.time && i.period === item.period || i.period > item.period;
        });
        return [
          item,
          exitLog
        ];
      });
      const logsInPeriod = scoreLogs.filter(item => periods.find(p => {
        let fromValid;
        let toValid;
        if (item.period === p[0].period && item.time === p[0].time) {
          fromValid = p[0].id < item.id;
        } else {
          fromValid = (item.time >= p[0].time && item.period === p[0].period) || item.period > p[0].period;
        }
        if (!p[1]) {
          toValid = true;
        } else if (item.period === p[1].period && item.time === p[1].time) {
          toValid = p[1].id > item.id;
        } else {
          toValid = (item.time <= p[1].time && item.period === p[1].period) || item.period < p[1].period;
        }
        return fromValid && toValid;
      }));
      const myTeamUsers = teamUserIds.indexOf(user.id) > -1 ? teamUserIds : competitorUserIds;
      const score = logsInPeriod.filter(item => myTeamUsers.indexOf(item.gameUserId) > -1)
        .reduce((acc, i) => acc + 1, 0);
      const lost = logsInPeriod.filter(item => myTeamUsers.indexOf(item.gameUserId) < 0)
        .reduce((acc, i) => acc + 1, 0);
      this.userEfficiency[user.id] = score - lost;

      let totalTime = periods.reduce((acc, p) => {
        let periodFrom = p[0].period;
        let timeFrom = p[0].time;
        let periodTo = p[1] && p[1].period;
        let timeTo = p[1] && p[1].time;
        if (!periodTo) {
          if (this.game.status === GameStatuses.closed) {
            if (this.overtimePeriods.length) {
              periodTo = Math.max(...this.overtimePeriods);
              timeTo = this.game.handballGameConfig.overtimeTime * 60;
            } else {
              periodTo = this.game.handballGameConfig.periodsCount;
              timeTo = this.game.handballGameConfig.periodTime * 60;
            }
          } else {
            periodTo = lastLog.period;
            timeTo = lastLog.time;
          }
        }
        if (period && (periodFrom > period || periodTo < period)) {
          return acc;
        }
        if (period) {
          if (periodFrom < period) {
            periodFrom = period;
            timeFrom = 0;
          }
          if (periodTo > period) {
            periodTo = period;
            timeTo = periodTo > this.game.handballGameConfig.periodsCount
              ? this.game.handballGameConfig.overtimeTime * 60
              : this.game.handballGameConfig.periodTime * 60;
          }
        }
        let time = timeTo - timeFrom;
        for (let i = periodFrom; i < periodTo; i++) {
          time += i > this.game.handballGameConfig.periodsCount
            ? this.game.handballGameConfig.overtimeTime * 60
            : this.game.handballGameConfig.periodTime * 60;
        }
        return acc + time;
      }, 0);
      totalTime = totalTime || 0;
      const minutes = Math.floor(totalTime / 60);
      const seconds = totalTime - minutes * 60;
      this.userMinutes[user.id] = `${minutes < 10 ? 0 : ''}${minutes}:${seconds < 10 ? 0 : ''}${seconds}`;
    }
  }

  updateGameUserStats(gameStatistic: HandballGameStatistic, logs: HandballGameLog[]) {
    const userLogs = logs.filter(item => item.gameUserId === gameStatistic.gameUserId);
    const logMapping = {
      shotMisses: HandballGameLogTypes.shot_miss,
      shotsOnGoal: HandballGameLogTypes.shot_on_goal,
      shotsBlocked: HandballGameLogTypes.shot_blocked,
      goals: HandballGameLogTypes.goal,
      penaltyGoals: HandballGameLogTypes.penalty_goal,
      penaltyMisses: HandballGameLogTypes.penalty_miss,
      penaltyShotsOnGoal: HandballGameLogTypes.penalty_shot_on_goal,
      assists: HandballGameLogTypes.assist,
      blockShots: HandballGameLogTypes.block_shot,
      saves: HandballGameLogTypes.save,
      fouls: HandballGameLogTypes.foul,
      yellowCards: HandballGameLogTypes.yellow_card,
      redCards: HandballGameLogTypes.red_card,
      steals: HandballGameLogTypes.steal,
      turnovers: HandballGameLogTypes.turnover
    };

    for (const key of Object.keys(logMapping)) {
        gameStatistic[key] = userLogs.filter(log => log.logType === logMapping[key]).length;
    }
    gameStatistic.points = gameStatistic.goals + gameStatistic.assists + gameStatistic.penaltyGoals;
    return gameStatistic;
  }

  getUserStatistic(userId: number, key: string): number {
    const userStatistic = this.filteredStatistic[userId];
    if (!userStatistic) {
      return 0;
    }
    return userStatistic[key] || 0;
  }

  identify(gameUser: GameUser): number {
    return gameUser.id;
  }

  getPercent(made, attempts): number {
    made = made || 0;
    attempts = attempts || 0;
    if (!made || !attempts) {
      return 0;
    }
    return Math.round(1000 * made / attempts) / 10;
  }
}
